import {Injectable} from "@angular/core";
import {NavigationAction, NavigationActionPage} from "@app/core/shared/domain/entities/navigation-action";
import {Router, UrlTree} from "@angular/router";
import {routesNames} from "@app/app-routing.module";
import {DisplayService} from "@app/presentation/services/display.service";
import {LocalizedRouter} from "@app/presentation/services/localized-router";

@Injectable({
    providedIn: 'root'
})
export class NavigationActionToRouteMapper {
    constructor(
        private router: Router,
        private displayService: DisplayService,
        private localizedRouter: LocalizedRouter
    ) {
    }

    /**
     * Maps a navigation action to a route.
     * If the route is not reachable, it will return null.
     * @param navigationAction
     */
    map(navigationAction: NavigationAction): UrlTree | null{
        const isMobileDisplay = this.displayService.isMobileDisplay;
        switch (navigationAction.page) {
            case NavigationActionPage.HOME:
                return this.router.createUrlTree(['/', routesNames.home]);
            case NavigationActionPage.CATALOG:
                const commands = [
                    '/',
                    routesNames.catalog,
                    //Add the category code to the url if it exists
                    ...(navigationAction.parameters?.categoryCode != null ? [navigationAction.parameters?.categoryCode] : [])
                ];
                return this.router.createUrlTree(commands, {queryParams: {f: navigationAction.parameters?.filters}});
            case NavigationActionPage.ORDERS:
                return this.router.createUrlTree(['/', routesNames.orderList]);
            case NavigationActionPage.COUPONS:
                return this.router.createUrlTree(['/', routesNames.coupons]);
            case NavigationActionPage.NEWS:
                return this.router.createUrlTree(['/', routesNames.catalog, 'N']);
            case NavigationActionPage.PROMOTIONS:
                return this.router.createUrlTree(['/', routesNames.catalog, 'Z']);
            case NavigationActionPage.LISTS:
                return this.router.createUrlTree(['/', routesNames.lists]);
            case NavigationActionPage.USUAL_PURCHASE:
                return this.router.createUrlTree(['/', routesNames.usualPurchase]);
            case NavigationActionPage.BEST_SELLERS:
                return this.router.createUrlTree(['/', routesNames.catalog, 'T']);
            case NavigationActionPage.COMPARATOR:
                return this.router.createUrlTree(['/', routesNames.comparison]);
            case NavigationActionPage.CHANGE_EMAIL:
                return this.router.createUrlTree(['/', routesNames.accountConfig, routesNames.changeEmail]);
            case NavigationActionPage.CHANGE_PASSWORD:
                return this.router.createUrlTree(['/',routesNames.accountConfig, routesNames.changePassword]);
            case NavigationActionPage.CHECKOUT:
                return this.router.createUrlTree(['/', routesNames.checkout]);
            case NavigationActionPage.LIST_DETAIL:
                if (navigationAction.parameters?.listId == null) {
                    //If the list id is not provided, we don't know which list to display.
                    return null;
                }
                return this.router.createUrlTree(['/', routesNames.listDetail, navigationAction.parameters.listId]);
            case NavigationActionPage.ACCOUNT:
                if (!isMobileDisplay) {
                    //If it's not mobile, the account is a side sheet.
                    //So we navigate to the home page and then navigate to the account.
                    this.localizedRouter.navigate(['/', routesNames.home], {skipLocationChange: true}).then(() => {
                        this.localizedRouter.navigate(['/', routesNames.account]);
                    });
                    return this.router.createUrlTree(['/', routesNames.home]);
                }
                return this.router.createUrlTree(['/', routesNames.account]);
            case NavigationActionPage.CATEGORIES:
                if (!isMobileDisplay) {
                    //If it's not mobile, the categories pages doesn't exist.
                    return null;
                }
                return this.router.createUrlTree(['/', routesNames.catalog, routesNames.categories]);
            case NavigationActionPage.PRODUCT_DETAIL:
                if (navigationAction.parameters?.productId == null) {
                    //If the product id is not provided, we don't know which product to display.
                    return null;
                }
                return this.router.createUrlTree(['/', routesNames.productDetails, navigationAction.parameters.productId]);
            case NavigationActionPage.PROMOTION_DETAIL:
                if (navigationAction.parameters?.promotionId == null) {
                    //If the promotion id is not provided, we don't know which promotion to display.
                    return null;
                }
                return this.router.createUrlTree(['/', routesNames.promotionDetail, navigationAction.parameters.promotionId]);
            case NavigationActionPage.LOGIN:
                const loginQueryParams = {
                    user: navigationAction.parameters?.user,
                    pwd: navigationAction.parameters?.password,
                };
                if (!isMobileDisplay) {
                    //If it's not mobile, the login page is displayed in a side sheet.
                    //So we navigate to the home page and then navigate to the login.
                    this.localizedRouter.navigate(['/', routesNames.home], {skipLocationChange: true}).then(() => {
                        this.localizedRouter.navigate(['/', routesNames.login], {queryParams: loginQueryParams});
                    });
                    return this.router.createUrlTree(['/', routesNames.home]);
                }
                return this.router.createUrlTree(['/', routesNames.login],{queryParams: loginQueryParams});
            case NavigationActionPage.SERVICE_UNAVAILABLE:
                const url = this.router.url;
                return this.router.createUrlTree(['/', routesNames.serviceUnavailable], {queryParams: {returnUrl: url}});
            case NavigationActionPage.SHARED:
                const productsIds = navigationAction.parameters?.products;
                this.localizedRouter.navigate(['/', routesNames.home], {skipLocationChange: true}).then(() => {
                    this.localizedRouter.navigate(['/', routesNames.shared], {queryParams: {products: productsIds}});
                });
                return this.router.createUrlTree(['/', routesNames.home]);
        }
    }
}
